import { VoiceConfig } from './voiceUtils';
import { findBestPatternMatch, splitCompoundCommands } from './voicePatternMatcher';
import { recordCommandResult, getSummaryMetrics } from "./commandMetrics";

// Command types for strong typing
export type CommandType = 'navigation' | 'search' | 'accessibility' | 'help' | 'custom' | 'unknown';

export interface CommandResult {
  type: CommandType;
  handled: boolean;
  response?: string;
  data?: any;
  action?: () => void;
}

export interface CommandPattern {
  pattern: RegExp;
  type: CommandType;
  extractParams?: (match: RegExpMatchArray) => any;
  description?: string;
  examples?: string[];
}

/**
 * Command Patterns Library Documentation
 * 
 * This section defines all supported voice command patterns organized by intent.
 * Each pattern includes:
 * - RegExp pattern to match user utterances
 * - Command type classification
 * - Parameter extraction logic
 * - Human-readable description
 * - Example utterances
 */

// Enhanced pattern libraries organized by intent
export const NAVIGATION_PATTERNS: CommandPattern[] = [
  {
    pattern: /^(?:go|navigate|open|show|take me|jump|head)(?:\s+to)?(?:\s+the)?(?:\s+([a-z\s]+)(?:\s+page)?)?$/i,
    type: 'navigation',
    extractParams: (match) => ({ page: match[1]?.trim() }),
    description: "Navigate to a specific page within the application",
    examples: ["go to home", "navigate to settings", "open dashboard", "show profile page"]
  },
  {
    pattern: /^(?:back|go back|return|previous)$/i,
    type: 'navigation',
    extractParams: () => ({ action: 'back' }),
    description: "Navigate back to the previous page",
    examples: ["back", "go back", "return", "previous page"]
  },
  {
    pattern: /^(?:home|dashboard|settings|profile|main page)$/i,
    type: 'navigation',
    extractParams: (match) => ({ page: match[0]?.trim() }),
    description: "Shortcut navigation to common pages",
    examples: ["home", "dashboard", "settings", "profile"]
  }
];

export const SEARCH_PATTERNS: CommandPattern[] = [
  {
    pattern: /^(?:search|find|look|query)(?:\s+for)?(?:\s+about)?(?:\s+(.+))$/i,
    type: 'search',
    extractParams: (match) => ({ query: match[1]?.trim() }),
    description: "Search for content within the application",
    examples: ["search for accessibility", "find products", "look for settings"]
  },
  {
    pattern: /^(?:where|how)(?:\s+(?:can|do)\s+i\s+find)(?:\s+(.+))$/i,
    type: 'search',
    extractParams: (match) => ({ query: match[1]?.trim() }),
    description: "Natural language search queries",
    examples: ["where can I find settings", "how do I find my profile"]
  }
];

export const ACCESSIBILITY_PATTERNS: CommandPattern[] = [
  {
    pattern: /^(?:enable|turn on|activate)\s+(?:dark mode|night mode)$/i,
    type: 'accessibility',
    extractParams: () => ({ feature: 'darkMode', value: true }),
    description: "Enable or disable dark mode",
    examples: ["enable dark mode", "turn on night mode"]
  },
  {
    pattern: /^(?:disable|turn off|deactivate)\s+(?:dark mode|night mode)$/i,
    type: 'accessibility',
    extractParams: () => ({ feature: 'darkMode', value: false }),
    description: "Disable or turn off dark mode",
    examples: ["disable dark mode", "turn off night mode"]
  },
  {
    pattern: /^(?:increase|larger|bigger)\s+(?:font|text|font size|text size)$/i,
    type: 'accessibility',
    extractParams: () => ({ feature: 'fontSize', action: 'increase' }),
    description: "Increase font size",
    examples: ["increase font size", "bigger text"]
  },
  {
    pattern: /^(?:decrease|smaller|reduce)\s+(?:font|text|font size|text size)$/i,
    type: 'accessibility',
    extractParams: () => ({ feature: 'fontSize', action: 'decrease' }),
    description: "Decrease font size",
    examples: ["decrease font size", "smaller text"]
  },
  {
    pattern: /^(?:reset|normal|default)\s+(?:font|text|font size|text size)$/i,
    type: 'accessibility',
    extractParams: () => ({ feature: 'fontSize', action: 'reset' }),
    description: "Reset font size to default",
    examples: ["reset font size", "default text size"]
  },
  {
    pattern: /^(?:enable|turn on|activate|show)\s+(?:highlight links|link highlighting)$/i,
    type: 'accessibility',
    extractParams: () => ({ feature: 'highlightLinks', value: true }),
    description: "Enable or show link highlighting",
    examples: ["enable link highlighting", "show link highlighting"]
  },
  {
    pattern: /^(?:disable|turn off|deactivate|hide)\s+(?:highlight links|link highlighting)$/i,
    type: 'accessibility',
    extractParams: () => ({ feature: 'highlightLinks', value: false }),
    description: "Disable or hide link highlighting",
    examples: ["disable link highlighting", "hide link highlighting"]
  },
  {
    pattern: /^(?:enable|turn on|activate)\s+(?:large cursor|big cursor|cursor size)$/i,
    type: 'accessibility',
    extractParams: () => ({ feature: 'largeCursor', value: true }),
    description: "Enable or increase cursor size",
    examples: ["enable large cursor", "increase cursor size"]
  },
  {
    pattern: /^(?:disable|turn off|deactivate)\s+(?:large cursor|big cursor|cursor size)$/i,
    type: 'accessibility',
    extractParams: () => ({ feature: 'largeCursor', value: false }),
    description: "Disable or decrease cursor size",
    examples: ["disable large cursor", "decrease cursor size"]
  }
];

export const HELP_PATTERNS: CommandPattern[] = [
  {
    pattern: /^(?:help|help me|what can (?:you|i) do|what (?:can|are) (?:you|i) say|commands|available commands|options|show help)$/i,
    type: 'help',
    description: "Get help with available commands",
    examples: ["help", "what can I do", "show help"]
  },
  {
    pattern: /^(?:how|what)(?:\s+(?:do|can)\s+i)(?:\s+(?:use|navigate|control))(?:\s+(?:this|the))?(?:\s+(?:website|site|app|page))?$/i,
    type: 'help',
    description: "Natural language help queries",
    examples: ["how do I navigate to home", "what can I do with this app"]
  }
];

// All patterns combined for easy iteration
export const ALL_PATTERNS: CommandPattern[] = [
  ...NAVIGATION_PATTERNS,
  ...SEARCH_PATTERNS,
  ...ACCESSIBILITY_PATTERNS,
  ...HELP_PATTERNS
];

/**
 * Normalizes a voice command by removing punctuation, 
 * collapsing whitespace, and converting to lowercase.
 * 
 * @param command - The raw voice command string
 * @returns Normalized command string
 */
export function normalizeCommand(command: string): string {
  return command
    .replace(/[.,!?;:]/g, '') // Remove punctuation
    .replace(/\s+/g, ' ') // Collapse whitespace
    .toLowerCase()
    .trim();
}

/**
 * Validates if an object conforms to the CommandPlugin interface
 * 
 * @param plugin - Object to validate
 * @returns Boolean indicating if the plugin is valid
 */
export function isValidCommandPlugin(plugin: any): plugin is import('./voiceUtils').CommandPlugin {
  if (typeof plugin !== 'object' || !plugin.triggers || typeof plugin.action !== 'function') return false;
  if (plugin.alias && !Array.isArray(plugin.alias)) return false;
  if (plugin.condition && typeof plugin.condition !== 'function') return false;
  return true;
}

/**
 * Finds a matching custom plugin for a given command
 * 
 * @param command - Normalized command string
 * @param config - Voice configuration with plugins
 * @param context - Optional context object (e.g. current page)
 * @returns Matching plugin or null
 */
function findCustomPlugin(command: string, config: import("./voiceUtils").VoiceConfig, context?: any) {
  const plugins = config.customCommandPlugins;
  if (!plugins) return null;
  for (const plugin of plugins) {
    if (!isValidCommandPlugin(plugin)) continue;
    // Check triggers and aliases
    let triggers = Array.isArray(plugin.triggers) ? plugin.triggers : [plugin.triggers];
    if (plugin.alias) triggers = triggers.concat(plugin.alias);
    for (const trig of triggers) {
      if (normalizeCommand(command) === normalizeCommand(trig)) {
        // Check condition if exists
        if (!plugin.condition || plugin.condition(command, context)) {
          return plugin;
        }
      }
    }
  }
  return null;
}

/**
 * Core function that processes a voice command and determines the appropriate action
 * 
 * @param command - The user's voice command as text
 * @param config - Configuration for voice processing
 * @returns CommandResult with type, handling status, and response
 */
export function processCommand(
  command: string,
  config: import('./voiceUtils').VoiceConfig
): CommandResult {
  if (!command || typeof command !== "string") {
    recordCommandResult(command ?? '', false);
    return {
      type: 'unknown',
      handled: false,
      response: "Sorry, I didn't catch that. Please speak again."
    };
  }

  // Enhancement: support compound commands ("do X and Y")
  const commands = splitCompoundCommands(command);

  if (commands.length > 1) {
    const results = commands.map(cmd => processCommand(cmd, config));
    let handled = false;
    let responses: string[] = [];
    results.forEach(r => {
      if (r.response) responses.push(r.response);
      if (r.handled) handled = true;
      if (r.action) r.action();
      // Track each subcommand
      recordCommandResult(command, r.handled);
    });
    return {
      type: results[0].type,
      handled,
      response: responses.join(' And then, '),
      data: { compound: true }
    };
  }

  const normalized = normalizeCommand(command);
  const bestMatch = findBestPatternMatch(normalized, ALL_PATTERNS);

  if (bestMatch && bestMatch.score > 0.7) {
    const { pattern } = bestMatch;
    const match = normalized.match(pattern.pattern);
    const params = pattern.extractParams && match ? pattern.extractParams(match) : {};
    recordCommandResult(command, true);
    return {
      type: pattern.type,
      handled: true,
      data: params,
      response: getResponseForCommand(pattern.type, params, config)
    };
  }

  const plugin = findCustomPlugin(command, config, { path: config.currentPath });
  if (plugin) {
    try {
      recordCommandResult(command, true);
      return {
        type: 'custom',
        handled: true,
        action: () => plugin.action(command, { path: config.currentPath }),
        response: plugin.description
          ? `Executing: ${plugin.description}`
          : `Executing custom command`
      }
    } catch (err) {
      console.error("Custom plugin error:", err);
      recordCommandResult(command, false);
      return {
        type: 'custom',
        handled: false,
        response: "Error executing your custom command plugin."
      };
    }
  }

  if (config.commands) {
    for (const [trigger, handler] of Object.entries(config.commands)) {
      const triggerNorm = normalizeCommand(trigger);
      const sim = similarity(normalized, triggerNorm);
      if (normalized.includes(triggerNorm) || sim > 0.8) {
        try {
          recordCommandResult(command, true);
          return {
            type: 'custom',
            handled: true,
            action: () => handler(normalized),
            response: `Executing command: ${trigger}`
          };
        } catch (err) {
          console.error('Error executing custom command:', err);
          recordCommandResult(command, false);
          return {
            type: 'custom',
            handled: false,
            response: "There was an error executing your command."
          };
        }
      }
    }
  }

  recordCommandResult(command, false);
  return {
    type: 'unknown',
    handled: false,
    response: config.customResponses?.notFound || "I'm sorry, I didn't understand that command. Try asking for help to see what I can do."
  };
}

// Get appropriate response based on command type and parameters
function getResponseForCommand(
  type: CommandType,
  params: any,
  config: VoiceConfig
): string {
  switch (type) {
    case 'navigation':
      if (params.action === 'back') {
        return "Going back to the previous page...";
      }
      
      if (params.page && config.routes) {
        // Check if page exists in routes
        for (const [routeName, routePath] of Object.entries(config.routes)) {
          if (normalizeCommand(params.page).includes(normalizeCommand(routeName))) {
            return `${config.customResponses?.navigate || 'Navigating to'} ${routeName}...`;
          }
        }
        return `I couldn't find a page called "${params.page}"`;
      }
      return "Where would you like to navigate to?";
      
    case 'search':
      if (params.query) {
        return `${config.customResponses?.searchPrefix || 'Searching for'} "${params.query}"`;
      }
      return "What would you like to search for?";
      
    case 'accessibility':
      if (params.feature === 'darkMode') {
        return params.value ? "Dark mode enabled" : "Dark mode disabled";
      } else if (params.feature === 'fontSize') {
        if (params.action === 'increase') return "Increasing font size";
        if (params.action === 'decrease') return "Decreasing font size";
        if (params.action === 'reset') return "Resetting font size to default";
      } else if (params.feature === 'highlightLinks') {
        return params.value ? "Link highlighting enabled" : "Link highlighting disabled";
      } else if (params.feature === 'largeCursor') {
        return params.value ? "Large cursor enabled" : "Large cursor disabled";
      }
      return "Adjusting accessibility settings";
      
    case 'help':
      return config.customResponses?.help || 
        "I can help you navigate the website, adjust accessibility settings, or search for content. " +
        "Try saying 'go to home', 'dark mode on', or 'search for accessibility'.";
      
    case 'custom':
      return "Executing custom command";
      
    default:
      return "I'm not sure how to help with that. Try asking for help to see what I can do.";
  }
}

// Add lightweight similarity for triggers in this file as well (identical to fuzzy in matcher)
function similarity(a: string, b: string): number {
  a = a.toLowerCase();
  b = b.toLowerCase();
  if (a === b) return 1;
  if (!a.length || !b.length) return 0;
  let longer = a.length >= b.length ? a : b;
  let shorter = a.length < b.length ? a : b;
  let edits = longer.length - shorter.length;
  for (let i = 0; i < shorter.length; i++) {
    if (shorter[i] !== longer[i]) edits++;
  }
  return 1 - edits / longer.length;
}
